import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  Search,
  RefreshCw,
  Eye,
  Calendar,
  Globe,
  FileJson,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface WebhookCall {
  id: string;
  path: string;
  headers: Record<string, any>;
  body: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

interface WebhookCallsResponse {
  data: WebhookCall[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const WebhookCalls: React.FC = () => {
  const { user } = useAuth();
  const [webhookCalls, setWebhookCalls] = useState<WebhookCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCall, setSelectedCall] = useState<WebhookCall | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const limit = 10;

  const fetchWebhookCalls = async (page: number = 1, search: string = '') => {
    try {
      setLoading(true);
      let url = `/admin/webhook-calls?page=${page}&limit=${limit}`;

      if (search.trim()) {
        url = `/admin/webhook-calls/search?path=${encodeURIComponent(search)}&page=${page}&limit=${limit}`;
      }

      const response = await api.get(url);
      const data: WebhookCallsResponse = response.data;

      setWebhookCalls(data.data);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching webhook calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchWebhookCalls(1, searchTerm);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchWebhookCalls(newPage, searchTerm);
  };

  const viewDetails = async (callId: string) => {
    try {
      const response = await api.get(`/admin/webhook-calls/${callId}`);
      setSelectedCall(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching webhook call details:', error);
      alert('Error loading webhook call details');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatJson = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  useEffect(() => {
    if (user?.role === 'super-admin' || user?.role === 'admin') {
      fetchWebhookCalls();
    }
  }, [user]);

  if (user?.role !== 'super-admin' && user?.role !== 'admin') {
    return (
      <div className='flex justify-center items-center py-16'>
        <Card>
          <CardContent className='text-center py-8'>
            <AlertTriangle className='w-12 h-12 text-yellow-500 mx-auto mb-4' />
            <h2 className='text-xl font-semibold mb-2'>Access Denied</h2>
            <p className='text-gray-600'>
              Only administrators can access this section.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
            Webhook Calls
          </h1>
          <p className='text-sm sm:text-base text-gray-600'>
            Monitor and inspect all webhook calls received by the system
          </p>
        </div>
      </div>

      {/* Search and Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Search Webhook Calls</CardTitle>
          <CardDescription>
            Search by path pattern or leave empty to view all calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex flex-col sm:flex-row gap-4'>
            <div className='flex-1'>
              <Input
                placeholder='Search by path (e.g., "jotform", "/webhook/jotform")'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className='flex gap-2'>
              <Button
                onClick={handleSearch}
                className='flex items-center gap-2'
              >
                <Search className='w-4 h-4' />
                Search
              </Button>
              <Button
                variant='outline'
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                  fetchWebhookCalls(1, '');
                }}
                className='flex items-center gap-2'
              >
                <RefreshCw className='w-4 h-4' />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle>
            Webhook Calls
            {total > 0 && (
              <span className='text-sm font-normal text-gray-500 ml-2'>
                ({total} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='text-center py-8'>
              <RefreshCw className='w-8 h-8 animate-spin mx-auto mb-4 text-gray-400' />
              <p className='text-gray-600'>Loading webhook calls...</p>
            </div>
          ) : webhookCalls.length === 0 ? (
            <div className='text-center py-8'>
              <FileJson className='w-12 h-12 text-gray-400 mx-auto mb-4' />
              <p className='text-gray-600'>
                {searchTerm
                  ? 'No webhook calls found matching your search.'
                  : 'No webhook calls found.'}
              </p>
            </div>
          ) : (
            <>
              {/* Webhook Calls List */}
              <div className='space-y-4'>
                {webhookCalls.map(call => (
                  <div
                    key={call.id}
                    className='border rounded-lg p-4 hover:bg-gray-50 transition-colors'
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex-1'>
                        <div className='flex items-center gap-2 mb-2'>
                          <Globe className='w-4 h-4 text-blue-500' />
                          <span className='font-mono text-sm bg-gray-100 px-2 py-1 rounded'>
                            {call.path}
                          </span>
                        </div>
                        <div className='flex items-center gap-2 text-sm text-gray-600'>
                          <Calendar className='w-4 h-4' />
                          <span>{formatDate(call.createdAt)}</span>
                        </div>
                      </div>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => viewDetails(call.id)}
                        className='flex items-center gap-2'
                      >
                        <Eye className='w-4 h-4' />
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex items-center justify-between mt-6'>
                  <p className='text-sm text-gray-600'>
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className='flex items-center gap-2'
                    >
                      <ChevronLeft className='w-4 h-4' />
                      Previous
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className='flex items-center gap-2'
                    >
                      Next
                      <ChevronRight className='w-4 h-4' />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      {showDetails && selectedCall && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <Card className='w-full max-w-4xl max-h-[90vh] overflow-hidden'>
            <CardHeader>
              <CardTitle className='flex items-center justify-between'>
                <span>Webhook Call Details</span>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setShowDetails(false)}
                >
                  Close
                </Button>
              </CardTitle>
              <CardDescription>
                Path: {selectedCall.path} | Received:{' '}
                {formatDate(selectedCall.createdAt)}
              </CardDescription>
            </CardHeader>
            <CardContent className='overflow-auto'>
              <div className='space-y-6'>
                {/* Basic Info */}
                <div>
                  <h3 className='text-lg font-semibold mb-3'>
                    Basic Information
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        ID
                      </label>
                      <p className='font-mono text-sm bg-gray-100 p-2 rounded'>
                        {selectedCall.id}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Path
                      </label>
                      <p className='font-mono text-sm bg-gray-100 p-2 rounded'>
                        {selectedCall.path}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Created At
                      </label>
                      <p className='text-sm bg-gray-100 p-2 rounded'>
                        {formatDate(selectedCall.createdAt)}
                      </p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Updated At
                      </label>
                      <p className='text-sm bg-gray-100 p-2 rounded'>
                        {formatDate(selectedCall.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Headers */}
                <div>
                  <h3 className='text-lg font-semibold mb-3'>
                    Request Headers
                  </h3>
                  <pre className='bg-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono'>
                    {formatJson(selectedCall.headers)}
                  </pre>
                </div>

                {/* Body */}
                <div>
                  <h3 className='text-lg font-semibold mb-3'>Request Body</h3>
                  <pre className='bg-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono'>
                    {formatJson(selectedCall.body)}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default WebhookCalls;
